import { ZfFormField, ZfInput, ZfStack } from '@zafire/react';
import moment from 'moment';
import { type FunctionComponent } from 'react';
import type { Control, UseFormSetValue, UseFormTrigger } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import type { ControllerRenderProps } from 'react-hook-form/dist/types/controller';
import { useTranslation } from 'react-i18next';
import type { EventType } from '../../../api/generated/client';
import { useYwuApp } from '../../../contexts/ywu-app-context';
import type { IAdvisoryOrLifeEventFormValues } from '../../../interfaces/event';
import { getDateFromIsoDateString, getIsoDateFromDateString } from '../../../utils/date-utils';
import { requiredField } from '../../../utils/form-utils';

interface IProps {
    eventType?: EventType;
    control: Control<IAdvisoryOrLifeEventFormValues>;
    setFormValue: UseFormSetValue<IAdvisoryOrLifeEventFormValues>;
    triggerValidation: UseFormTrigger<IAdvisoryOrLifeEventFormValues>;
}

export const NeedEventModalDateFields: FunctionComponent<IProps> = ({ eventType, control, setFormValue, triggerValidation }) => {
    const { t } = useTranslation();
    const { isPresentationMode } = useYwuApp();

    const convertZafireDateToDate = (date: string) => getIsoDateFromDateString(getDateFromIsoDateString(date));

    const onDueDateChange = (dateValue: string, fieldOnChange: ControllerRenderProps['onChange']) => {
        const date = convertZafireDateToDate(dateValue);
        fieldOnChange(date);

        if (eventType === 'ADVISORY_NEED_TASK') {
            setFormValue('reminderDate', convertZafireDateToDate(moment(date).clone().subtract({ days: 7, months: 0 }).toISOString()));
        } else {
            setFormValue('reminderDate', convertZafireDateToDate(moment(date).clone().subtract({ days: 0, months: 3 }).toISOString()));
        }

        triggerValidation('reminderDate');
    };

    return (
        <ZfStack direction="row" spacing="medium" justify="space-between">
            <Controller
                name="dueDate"
                control={control}
                rules={{ required: requiredField(t('dateFieldRequired')) }}
                render={({ field, fieldState }) => (
                    <ZfFormField
                        label={t('lifeEventDueDate')}
                        mandatory={true}
                        className={isPresentationMode ? 'w-100' : 'w-50'}
                        alerts={fieldState.error?.message ? [fieldState.error.message] : undefined}
                    >
                        <ZfInput
                            type="date"
                            value={field.value}
                            error={fieldState.invalid}
                            onInputChange={(e) => onDueDateChange(e.detail, field.onChange)}
                            onBlur={field.onBlur}
                        />
                    </ZfFormField>
                )}
            />
            {!isPresentationMode && (
                <Controller
                    name="reminderDate"
                    control={control}
                    rules={{ required: requiredField(t('dateFieldRequired')) }}
                    render={({ field, fieldState }) => (
                        <ZfFormField
                            label={t('reminderDate')}
                            mandatory={true}
                            className="w-50"
                            alerts={fieldState.error?.message ? [fieldState.error.message] : undefined}
                        >
                            <ZfInput
                                type="date"
                                value={field.value}
                                error={fieldState.invalid}
                                onInputChange={(e) => field.onChange(convertZafireDateToDate(e.detail))}
                                onBlur={field.onBlur}
                            />
                        </ZfFormField>
                    )}
                />
            )}
        </ZfStack>
    );
};
